'use client';

import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Link as MuiLink, Button, Paper } from '@mui/material';
import { TextField } from '@mui/material'; // Added TextField import

const TermsPage = () => {
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState(''); // State for search term

  useEffect(() => {
    const extractHeadings = () => {
      const headingElements = Array.from(document.querySelectorAll('h2'));
      const extracted = headingElements.map((heading, index) => {
        const id = heading.textContent ? heading.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '') : `section-${index}`;
        heading.id = id;
        return { id, text: heading.textContent || '' };
      });
      setHeadings(extracted);
    };

    extractHeadings();
  }, []);

  const lastUpdated = "November 8, 2025"; // Manually set for now

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    // Use lazy facade to avoid bundling jspdf/html2canvas into initial JS
    const { generatePdfFromElement } = await import('@/lib/pdf/pdfFacade');
    const content = document.getElementById('terms-content');
    if (content) {
      const pdf = await generatePdfFromElement(content);
      pdf.save('floai-terms-and-conditions.pdf');
    }
  };

  // Helper function to highlight text
  const highlightText = (text: string) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => (
          <span
            key={i}
            style={part.toLowerCase() === searchTerm.toLowerCase() ? { backgroundColor: 'yellow', fontWeight: 'bold' } : {}}
          >
            {part}
          </span>
        ))}
      </>
    );
  };

  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, py: 4, gap: 4 }}>
        <Box sx={{ flexGrow: 1, pr: { xs: 0, md: 3 } }} id="terms-content">
          <Typography variant="h4" component="h1" fontWeight={700} mb={2} color="text.primary">
            Terms and Conditions
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" mb={4}>
            Last Updated: {lastUpdated}
          </Typography>

          <TextField
            label="Search Terms"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 4 }}
          />

          <h2>{highlightText("Welcome to FloAI!")}</h2>
          <p>{highlightText("These terms and conditions outline the rules and regulations for the use of FloAI&apos;s Website, located at [Your Website Address].")}</p>

          <p>{highlightText("By accessing this website we assume you accept these terms and conditions. Do not continue to use FloAI if you do not agree to take all of the terms and conditions stated on this page.")}</p>

          <h2>{highlightText("Cookies:")}</h2>
          <p>{highlightText("The website uses cookies to help personalize your online experience. By accessing FloAI, you agreed to use the required cookies.")}</p>

          <h2>{highlightText("License:")}</h2>
          <p>{highlightText("Unless otherwise stated, FloAI and/or its licensors own the intellectual property rights for all material on FloAI. All intellectual property rights are reserved. You may access this from FloAI for your own personal use subjected to restrictions set in these terms and conditions.")}</p>

          <p>{highlightText("You must not:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li>{highlightText("Republish material from FloAI")}</li>
              <li>{highlightText("Sell, rent or sub-license material from FloAI")}</li>
              <li>{highlightText("Reproduce, duplicate or copy material from FloAI")}</li>
              <li>{highlightText("Redistribute content from FloAI")}</li>
            </ul>
          </Box>

          <p>{highlightText("This Agreement shall begin on the date hereof.")}</p>

          <p>{highlightText("Parts of this website offer an opportunity for users to post and exchange opinions and information in certain areas of the website. FloAI does not filter, edit, publish or review Comments prior to their presence on the website. Comments do not reflect the views and opinions of FloAI,&apos;its agents and/or affiliates. Comments reflect the views and opinions of the person who post their views and opinions. To the extent permitted by applicable laws, FloAI shall not be liable for the Comments or for any liability, damages or expenses caused and/or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website.")}</p>

          <p>{highlightText("FloAI reserves the right to monitor all Comments and to remove any Comments which can be considered inappropriate, offensive or causes breach of these Terms and Conditions.")}</p>

          <p>{highlightText("You warrant and represent that:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li>{highlightText("You are entitled to post the Comments on our website and have all necessary licenses and consents to do so;")}</li>
              <li>{highlightText("The Comments do not invade any intellectual property right, including without limitation copyright, patent or trademark of any third party;")}</li>
              <li>{highlightText("The Comments do not contain any defamatory, libelous, offensive, indecent or otherwise unlawful material which is an invasion of privacy")}</li>
              <li>{highlightText("The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity.")}</li>
            </ul>
          </Box>

          <p>{highlightText("You hereby grant FloAI a non-exclusive license to use, reproduce, edit and authorize others to use, reproduce and edit any of your Comments in any and all forms, formats or media.")}</p>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handlePrint}>Print</Button>
            <Button variant="contained" onClick={handleDownloadPDF}>Download PDF</Button>
          </Box>

        </Box>

        <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
          <Paper elevation={3} sx={{ position: 'sticky', top: 100, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              On this page
            </Typography>
            <Box component="nav">
              {headings.map((heading) => (
                <MuiLink
                  key={heading.id}
                  href={`#${heading.id}`}
                  sx={{
                    display: 'block',
                    py: 0.5,
                    pl: 1,
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    borderLeft: '2px solid transparent',
                    '&.active': { borderLeftColor: 'primary.main', color: 'primary.main', fontWeight: 600 },
                  }}
                >
                  {heading.text}
                </MuiLink>
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    </PublicPageLayout>
  );
};

export default TermsPage;
